import { useEffect, useState } from "react";
import { useAuth } from '@/provider/UserProvider';
import useBalance from '@/hooks/useBalance';
import { TokenDenom } from "@/hooks/useTokenPrices";
import { getMessageExecuteMint, getMessageExecuteMintFree, mintExecute, queryConfigMintNFT } from "./helpers";
import { configs } from "@/configs";
import { ActionSSO, InputExecuteSSO, ssoExecute } from "@/libs/ssoWallet";
import { network } from "@/configs/network";
import { toast } from "react-toastify";
import { EVENT_COLLECTION_ID } from "./useFetch";

const isProduction = process.env.NEXT_PUBLIC_NODE_ENV === 'production';

export const MAX_ITEM_MINT = 1000;

export const PRICE_MINT_FREE = 0;
export const PRICE_MINT_PHASE_2 = isProduction ? 5000 : 1;
export const PRICE_MINT_PHASE_3 =isProduction ? 10000 : 2;

export const MAX_ITEM_FREE = isProduction ? 100 : 5;
export const MAX_ITEM_PHASE_2 = isProduction ? 400 : 5;
export const MAX_ITEM_PHASE_3 = isProduction ? 500 : 990;

const useMint = (callback: () => void, phase: '1' | '2' | '3') => {
  const [valid, setValid] = useState('');
  const [err, setErr] = useState('');
  const [txHash, setTxHash] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [loading, setLoading] = useState(false);
  const { auth, isLoginClient } = useAuth();
  const { airi, orai } = useBalance([TokenDenom.AIRI, TokenDenom.ORAI]);
  const balanceConverted = airi * 10 ** -6;
  const oraiBalance = orai * 10 ** -6;;
  const quantityNum = Number(quantity ?? 1);
  const quantityCeil = Math.ceil(quantityNum);

  const getTotalPrice = () => {
    if (phase === '2') return quantityNum < 1 ? PRICE_MINT_PHASE_2 : quantityCeil * PRICE_MINT_PHASE_2;
    if (phase === '3') return quantityNum < 1 ? PRICE_MINT_PHASE_3 : quantityCeil * PRICE_MINT_PHASE_3;
    return PRICE_MINT_FREE;
  }

  const totalPrice = getTotalPrice();

  const getAmount = () => {
    if (phase === '2') return ((PRICE_MINT_PHASE_2 * 10 ** 6) * quantityCeil).toString();
    if (phase === '3') return ((PRICE_MINT_PHASE_3 * 10 ** 6) * quantityCeil).toString();
    return PRICE_MINT_FREE.toString();
  }

  const upQuantity = (inputRef: any) => {
    if (Number(quantity) >= MAX_ITEM_MINT) {
      inputRef.current.value = MAX_ITEM_MINT;
      setQuantity(MAX_ITEM_MINT.toString());
    } else {
      inputRef.current.value = Number(quantity) + 1;
      setQuantity((Number(quantity) + 1).toString())
    }
  }
  const downQuantity = (inputRef: any) => {
    if (Number(quantity) <= 1) {
      inputRef.current.value = 1;
      setQuantity('1');
    } else {
      inputRef.current.value =Number(quantity) - 1;
      setQuantity((Number(quantity) - 1).toString())
    }
  }
  const handleChangeQuantity = (value: string, inputRef: any) => {
    if (!value) {
      setQuantity('1');
      return;
    };
    if (Number(value) > MAX_ITEM_MINT) {
      inputRef.current.value = MAX_ITEM_MINT;
      setQuantity(MAX_ITEM_MINT.toString());
    } else if (Number(value) < 1) {
      inputRef.current.value = 1;
      setQuantity('1');
    } else {
      setQuantity(value)
    }
  }

  const handleWhenSuccess = async () => {
    toast.success('Minted successfully, You can view in profile!');
    callback();
  }

  const resetState = () => {
    setTxHash('');
    setErr('');
  }

  const handleExecuteWithOwallet = async (msg: string, isFree: boolean) => {
    const res = await mintExecute({ contract: isFree ? configs.launchpadContractAddress : configs.airiTokenContract, msg });
    if (res.tx_response.txhash) {
      setTxHash(res.tx_response.txhash)
    }
  }

  const handleExecuteWithSSO = async (msg: string, isFree: boolean) => {
    const input: InputExecuteSSO = {
      contractAddress: isFree ? configs.launchpadContractAddress : configs.airiTokenContract,
      chainId: network?.chainId || 'Oraichain',
      params: msg,
    };
    await ssoExecute(input, ActionSSO.launchpad);
  }

  const isPassRules = async (isFinish: boolean, countItems: number) => {
    if (!isLoginClient) {
      toast.warning("Please login to continue!");
      return false;
    }

    if (totalPrice > balanceConverted) {
      toast.warning("You don't have enough funds to pay for these tokens");
      return false;
    }

    if (oraiBalance === 0) {
      toast.warning("You don't have enough transaction fees (ORAI)");
      return false;
    }

    if (isFinish) {
      toast.error("The event has ended");
      return false;
    }

    const hasMaxItemCanMint = countItems >= MAX_ITEM_MINT;
    if (hasMaxItemCanMint) {
      toast.error("Maximum number of NFT has been reached");
      return false;
    }

    const res = await queryConfigMintNFT({
      contract: configs.launchpadContractAddress,
      collection: EVENT_COLLECTION_ID,
    });
    const totalMinted = res.data?.total_already_mint;
    if (totalMinted) {
      const maxTotalItemToPhase2 = MAX_ITEM_PHASE_2 + MAX_ITEM_FREE;
      const isInPhase2 = maxTotalItemToPhase2 > Number(totalMinted);
      const isPassRuleInPhase2 = (Number(totalMinted) + quantityCeil) <= maxTotalItemToPhase2;
      if (isInPhase2 && !isPassRuleInPhase2) {
        toast.warning(`Currently, in phase 2, the maximum number is ${maxTotalItemToPhase2 - Number(totalMinted)}`);
        return false;
      }

      const numCanMintLeft = MAX_ITEM_MINT - Number(totalMinted);
      const isCanMintWhenCheckNumLeft = numCanMintLeft>= quantityCeil;
      if (!isCanMintWhenCheckNumLeft) {
        toast.warning(`The Maximum number allowed is ${numCanMintLeft}`);
        return false;
      }
    }
    return true;
  }

  const handleSubmit = async ({
    collection,
    isFinish,
    countItems,
    isFree,
  }:{
    isFinish: boolean,
    collection: number,
    countItems: number,
    isFree: boolean,
  }) => {
    const isPassRule = await isPassRules(isFinish, countItems);
    if (!isPassRule) return;
    
    resetState();
    setLoading(true);
    try {
      const amount = getAmount();
      const totalItem = isFree ? '1' : quantityCeil.toString();

      const msg = isFree ? 
        getMessageExecuteMintFree({
          tokenContract: configs.airiTokenContract,
          collection,
          totalItem,
          userAddress: auth.user.publicAddress
        }) : 
        getMessageExecuteMint({
          contract: configs.launchpadContractAddress,
          collection,
          totalItem,
          amount,
      });
      if (auth.isOwallet) {
        await handleExecuteWithOwallet(msg, isFree);
      } else {
        await handleExecuteWithSSO(msg, isFree)
      }
    } catch (error: any) {
      callback();
      console.error("error mint nft: ", error);
      toast.error("There is some error, please try again!");
      setLoading(false);
    }
  }

  // for sso
  useEffect(() => {
    if (typeof window !== 'undefined' && !auth.isOwallet) {
      window.addEventListener(
        'message',
        (event) => {
          if (event.origin !== configs.ssoUrl) return;
          const data = JSON.parse(event?.data || '');
          if (data.action !== ActionSSO.launchpad) return;
          if (data.status === 'success' && data.response) {
            setTxHash(data.response.transactionHash);
          } else {
            setErr(data.response);
          }
        },
        false,
      );
    }
    return window.removeEventListener('message', () => {});
  }, []);

  useEffect(() => {
    if (err) {
      setLoading(false);
      toast.error(err);
    }
  }, [err]);

  useEffect(() => {
    if (txHash) {
      setLoading(false);
      handleWhenSuccess();
    }
  }, [txHash]);

  return {
    handleSubmit,
    loading,
    valid,
    setValid,
    totalPrice,
    upQuantity,
    downQuantity,
    handleChangeQuantity,
  }

}

export default useMint;